import React, { useEffect, useState } from 'react';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { ReactNode } from 'react';
import DrawerSwipeBtn from './DrawerSwipeBtn';
import { useViewportWidth } from '@/hooks/shared';
import { Icon } from '@/components/shared/display/Icon';
import { CloseIcon } from '@/assets/Icons';

interface BaseDrawerProps {
  children: ReactNode;
  rounded?: boolean;
  content?: ReactNode | string;
  customCloseTrigger?: ReactNode;
  title?: ReactNode | string;
  showHeaderDivider?: boolean;
  direction?: 'top' | 'left' | 'right' | 'bottom';
  className?: string;
  isOpen?: boolean; // External state control
  isMobile?: boolean; // External state control
  onClose?: () => void; // External close handler
  customContent?: ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export const BaseDrawer = ({
  children,
  rounded = true,
  content,
  title,
  showHeaderDivider = false,
  direction = 'left',
  className,
  isOpen, // External state control
  onClose,
  onOpenChange,
  customContent,
}: BaseDrawerProps) => {
  if (!content && !customContent) return <>{children}</>;
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const { isMobile } = useViewportWidth();

  // Close drawer, use external `onClose` if available, otherwise use internal state
  const closeDrawer = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  // Open drawer
  const openDrawer = () => {
    if (onClose) {
      setInternalIsOpen(true);
    } else {
      setInternalIsOpen(true);
    }
  };

  // Sync external state (`isOpen`) with internal state (`internalIsOpen`)
  useEffect(() => {
    if (isOpen !== undefined) {
      setInternalIsOpen(isOpen); // Sync external `isOpen` prop with internal state
    }
  }, [isOpen]);

  return (
    <Drawer
      open={isOpen !== undefined ? isOpen : internalIsOpen}
      onOpenChange={(open: boolean) => {
        if (isOpen !== undefined) {
          // controlled
          if (!open) {
            onClose?.(); // tells parent to close the drawer
          }
        } else {
          setInternalIsOpen(open); // uncontrolled, update internal state
        }
        onOpenChange?.(open);
      }}
      direction={direction}
    >
      <DrawerTrigger asChild onClick={openDrawer}>
        {children}
      </DrawerTrigger>
      {!customContent ? (
        <DrawerContent
          rounded={rounded}
          className={`${isMobile && direction === 'bottom' ? 'pt-10' : 'h-full'} ${className} before:bg-transparent`}
        >
          <DrawerHeader className={`${showHeaderDivider ? 'border-b border-neutral-100' : ''}`}>
            {isMobile && direction === 'bottom' && <DrawerSwipeBtn variant="top" />}

            <DrawerTitle className="flex justify-between items-center">
              {title}
              <DrawerClose className="w-100 flex justify-end h-full items-center " onClick={closeDrawer}>
                <Icon icon={CloseIcon} />
              </DrawerClose>
            </DrawerTitle>
            {/* <DrawerDescription>{title}</DrawerDescription> */}
          </DrawerHeader>
          {content}
          {/* <DrawerFooter></DrawerFooter> */}
        </DrawerContent>
      ) : (
        <>{customContent}</>
      )}
    </Drawer>
  );
};
